import React, { useState, useEffect, useRef } from 'react';
import { clientsAPI } from '../../../api';
import { resolveUrl, fmtDate } from '../utils';
import HistoryEntryEditor from './HistoryEntryEditor';
import HistoryEntryView from './HistoryEntryView';

const tagChip = {
  display: 'inline-block', padding: '3px 10px', fontSize: 10.5, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: 'var(--kahla-text-light)',
  border: '1px solid var(--kahla-border)', borderRadius: 999,
};

export const HistorySection = ({ clientId, t, lang, boards, documents, showToast }) => {
  const [entries, setEntries] = useState(null);
  const [q, setQ] = useState('');
  const [editorEntry, setEditorEntry] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [viewEntry, setViewEntry] = useState(null);
  const debounceRef = useRef(null);

  const fetchEntries = (query = q) => {
    clientsAPI.history.list(clientId, query).then(({ data }) => setEntries(data)).catch(() => setEntries([]));
  };

  useEffect(() => { fetchEntries(''); /* eslint-disable-next-line */ }, [clientId]);

  const handleSearch = (val) => {
    setQ(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchEntries(val), 300);
  };

  const onSaved = () => { setEditorEntry(undefined); fetchEntries(); };

  return (
    <section data-testid="client-history-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
        <input
          data-testid="history-search-input"
          value={q}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t.cs_search_history || 'Search title, notes, tags…'}
          style={{
            flex: '1 1 260px', maxWidth: 380, padding: '9px 0', border: 'none',
            borderBottom: '1px solid var(--kahla-border)', background: 'transparent',
            fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
            fontSize: 15, color: 'var(--kahla-charcoal)', outline: 'none',
          }}
        />
        <button className="btn-primary" data-testid="add-history-entry-btn" onClick={() => setEditorEntry(null)}>
          {t.cs_add_entry || '+ Add entry'}
        </button>
      </div>

      {entries === null ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>
      ) : entries.length === 0 ? (
        <div data-testid="history-empty" style={{
          padding: '48px 24px', border: '1px dashed var(--kahla-border)', textAlign: 'center',
          fontFamily: 'Cormorant Garamond, Playfair Display, serif', fontStyle: 'italic',
          fontSize: 15, color: 'var(--kahla-text-light)', lineHeight: 1.6,
        }}>
          {q ? (t.cs_history_no_results || 'Nothing matches your search.') : (t.cs_history_empty || 'The story of this collaboration starts here — add the first entry.')}
        </div>
      ) : (
        <div style={{ maxWidth: 860 }}>
          {entries.map((e) => (
            <article
              key={e.id}
              data-testid={`history-entry-${e.id}`}
              onClick={() => setViewEntry(e)}
              style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 0, cursor: 'pointer' }}
            >
              {/* Timeline rail: date + dot */}
              <div style={{ paddingTop: 18, textAlign: 'right', paddingRight: 20, position: 'relative' }}>
                <p style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--kahla-text-light)', margin: 0, fontFamily: 'Playfair Display, serif', lineHeight: 1.4 }}>
                  {fmtDate(e.date, lang)}
                </p>
                <span aria-hidden="true" style={{
                  position: 'absolute', right: -3.5, top: 22, width: 7, height: 7, borderRadius: '50%',
                  background: '#cf9a3d',
                }} />
              </div>
              {/* Entry content */}
              <div style={{ borderLeft: '1px solid var(--kahla-border)', padding: '16px 4px 18px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 16, fontWeight: 400, color: 'var(--kahla-charcoal)', margin: 0 }}>
                      {e.title}
                    </h3>
                    {e.note && (
                      <p style={{
                        fontSize: 13, color: 'var(--kahla-text-light)', margin: '4px 0 0', lineHeight: 1.55,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>{e.note}</p>
                    )}
                    {(e.tags?.length > 0) && (
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
                        {e.tags.map((tag) => <span key={tag} style={tagChip}>{tag}</span>)}
                      </div>
                    )}
                  </div>
                  {(e.photos?.length > 0) && (
                    <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                      {e.photos.slice(0, 3).map((p) => (
                        <img key={p.id} src={resolveUrl(p.url)} alt="" style={{ width: 36, height: 36, objectFit: 'cover' }} />
                      ))}
                      {e.photos.length > 3 && (
                        <span style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--kahla-text-light)', border: '1px solid var(--kahla-border)' }}>
                          +{e.photos.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editorEntry !== undefined && (
        <HistoryEntryEditor
          clientId={clientId}
          entry={editorEntry}
          boards={boards}
          documents={documents}
          t={t}
          onSaved={onSaved}
          onClose={() => setEditorEntry(undefined)}
          showToast={showToast}
        />
      )}

      {viewEntry && (
        <HistoryEntryView
          clientId={clientId}
          entryId={viewEntry.id}
          boards={boards}
          documents={documents}
          t={t}
          lang={lang}
          onEdit={(full) => { setViewEntry(null); setEditorEntry(full); }}
          onDeleted={() => { setViewEntry(null); fetchEntries(); }}
          onClose={() => setViewEntry(null)}
          showToast={showToast}
        />
      )}
    </section>
  );
};

export default HistorySection;
